import { DSInput } from "@components/design-system/molecules";
import React, { useEffect, useState } from "react";

import CorporateGroupsList from "../../../assets/data/CGroups";
import { useCurrentUser } from "../../../hooks/use-current-user";
import StepWrapper from "../StepWrapper";
import {
  findGroupByCriteria,
  filterGroupsByLabel,
  isEmailDomainMatch,
} from "../utils/groupNameUtils";

export default function GroupNameStep({
  clinicGroup,
  setClinicGroup,
}) {
  const { data } = useCurrentUser();

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState(
    CorporateGroupsList,
  );

  const currentGroup = findGroupByCriteria("value", clinicGroup);
  const inputValue = currentGroup
    ? currentGroup.label
    : clinicGroup || "";

  useEffect(() => {
    if (data?.user && !clinicGroup) {
      const userDomain = data?.user?.email?.split("@")[1];
      const matchedGroup = findGroupByCriteria("email", userDomain);
      if (matchedGroup) {
        setClinicGroup(matchedGroup.value);
      }
    }
  }, [data?.user, clinicGroup, setClinicGroup]);

  const handleInputChange = (e) => {
    const value = e.target.value;

    if (value.trim()) {
      const filtered = filterGroupsByLabel(value);
      setFilteredGroups(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredGroups(CorporateGroupsList);
      setShowSuggestions(false);
    }
    setClinicGroup(value);
  };

  const handleSuggestionClick = (group) => {
    setClinicGroup(group.value);
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    if (inputValue.trim()) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow the user to actually click the option
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <StepWrapper
      title="Are you or your clinic affiliated with a group?"
      description="If your clinic is affiliated with a group or consolidator, enter it below."
      maxWidth="max-w-[90vw] md:max-w-[40vw]"
      withBackground={false}
    >
      <div className="flex flex-row items-center justify-center space-x-4 w-full">
        <div className="relative flex-1">
          <DSInput
            type="text"
            dataTestId="clinic-group-input"
            placeholder="Group/Consolidator Name"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />

          {showSuggestions && filteredGroups.length > 0 && (
            <div className="absolute z-50 w-full mt-1 max-h-[200px] overflow-auto rounded-lg shadow-md bg-input border border-secondary">
              {filteredGroups.map((group) => (
                <div
                  key={group.value}
                  className="px-3 py-2 cursor-pointer hover:bg-secondary text-primary"
                  onClick={() => handleSuggestionClick(group)}
                >
                  {group.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isEmailDomainMatch(data?.user?.email, currentGroup?.email) && (
        <div className="bg-brand-subtle text-brand font-semibold p-3 rounded-xl mt-4">
          We've detected that you might be part of{" "}
          {currentGroup?.label} based on your email address.
        </div>
      )}
    </StepWrapper>
  );
}
